<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { EMOJIS } from "@shared/lib/emojis";
import AppButtonSmall from "@shared/ui/components/AppButtonSmall.vue";
import AppEditor from "@shared/ui/components/AppEditor.vue";
import AppMenu from "@shared/ui/components/AppMenu.vue";
import IconFile from "@shared/ui/icons/IconFile.vue";
import IconMic from "@shared/ui/icons/IconMic.vue";
import IconSmile from "@shared/ui/icons/IconSmile.vue";
import IconVideo from "@shared/ui/icons/IconVideo.vue";

import FileUploader from "@features/file-transfer/FileUploader.vue";
import AudioRecorderModal from "@features/media-recorder/AudioRecorderModal.vue";
import VideoRecorderModal from "@features/media-recorder/VideoRecorderModal.vue";

import type { FileAttachment } from "shared";

const props = defineProps<{
    disabled: boolean;
    chatId: string;
    editingNonce: string | null;
    editingText: string;
    replyPreview: { author?: string; text: string } | null;
}>();

const emit = defineEmits<{
    send: [text: string, files?: FileAttachment[]];
    editSubmit: [nonce: string, newText: string];
    editCancel: [];
    cancelReply: [];
    typing: [];
    stopTyping: [];
}>();

const uploaderRef = ref<InstanceType<typeof FileUploader> | null>(null);
const editorRef = ref<InstanceType<typeof AppEditor> | null>(null);
const editEditorRef = ref<InstanceType<typeof AppEditor> | null>(null);

const sendText = ref("");
const editText = ref("");

// Есть ли что отправить — влияет на вид кнопки (залитая acid vs призрачная).
// Файлы уходят отдельным потоком через модалку FileUploader.
const canSend = computed(() => {
    const stripped = sendText.value.replace(/<[^>]*>/g, "").trim();
    return !props.disabled && stripped.length > 0;
});

watch(
    () => props.editingText,
    (val) => {
        editText.value = val;
        editEditorRef.value?.setContent(val);
    },
    { immediate: true }
);

function send() {
    const html = sendText.value;
    const stripped = html.replace(/<[^>]*>/g, "").trim();
    if (!stripped) {
        return;
    }
    emit("stopTyping");
    emit("send", html);
    sendText.value = "";
    editorRef.value?.clear();
}

// Отправка из модалки файлов: подпись + вложения одним сообщением.
function sendFiles(caption: string, files: FileAttachment[]) {
    emit("stopTyping");
    emit("send", caption, files);
}

// Вставка эмодзи из пикера в позицию курсора (пикер не закрывается —
// можно вставить несколько подряд; закрытие — клик снаружи / Esc).
function insertEmoji(emoji: string) {
    editorRef.value?.insertText(emoji);
}

// Запись видео/аудиосообщения (модалки с камерой / волнами).
const videoRecorderOpen = ref(false);
const audioRecorderOpen = ref(false);

function sendRecording(attachment: FileAttachment) {
    emit("stopTyping");
    emit("send", "", [attachment]);
}

// Drag-and-drop файлов на область ввода.
const dragActive = ref(false);
let dragDepth = 0;

function onDragEnter() {
    if (props.disabled || props.editingNonce) {
        return;
    }
    dragDepth++;
    dragActive.value = true;
}

function onDragLeave() {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) {
        dragActive.value = false;
    }
}

function onDrop(e: DragEvent) {
    dragDepth = 0;
    dragActive.value = false;
    if (props.disabled || props.editingNonce) {
        return;
    }
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length) {
        void uploaderRef.value?.addFiles(files);
    }
}

function submitEdit() {
    if (!props.editingNonce) {
        return;
    }
    const html = editText.value;
    const stripped = html.replace(/<[^>]*>/g, "").trim();
    if (stripped && html !== props.editingText) {
        emit("editSubmit", props.editingNonce, html);
    } else {
        emit("editCancel");
    }
}
</script>

<template>
    <div
        class="chat-editor"
        :class="{ 'chat-editor_drag': dragActive }"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
    >
        <div v-if="dragActive" class="chat-editor__drop">Отпустите файлы</div>
        <template v-if="editingNonce">
            <div class="chat-editor__box">
                <div class="chat-editor__input-row">
                    <AppEditor
                        ref="editEditorRef"
                        v-model="editText"
                        placeholder="Редактировать…"
                        autofocus
                        @submit="submitEdit"
                    />
                    <button class="chat-editor__btn" @click="submitEdit">
                        Сохранить
                    </button>
                    <button
                        class="chat-editor__btn"
                        @click="emit('editCancel')"
                    >
                        Отмена
                    </button>
                </div>
            </div>
        </template>

        <template v-else>
            <div v-if="replyPreview" class="chat-editor__reply">
                <span class="chat-editor__reply-bar" />
                <div class="chat-editor__reply-body">
                    <span class="chat-editor__reply-author">
                        Ответ · {{ replyPreview.author }}
                    </span>
                    <span class="chat-editor__reply-text">
                        {{ replyPreview.text }}
                    </span>
                </div>
                <button
                    class="chat-editor__reply-cancel"
                    @click="emit('cancelReply')"
                >
                    ✕
                </button>
            </div>

            <div class="chat-editor__box">
                <!-- Модалка превью выбранных файлов + скрытый input (кнопку
                     рисует панель). Отправка вложений идёт из модалки. -->
                <FileUploader
                    ref="uploaderRef"
                    :chat-id="chatId"
                    :disabled="disabled"
                    @send="sendFiles"
                />

                <VideoRecorderModal
                    v-if="videoRecorderOpen"
                    :chat-id="chatId"
                    @send="sendRecording"
                    @close="videoRecorderOpen = false"
                />

                <AudioRecorderModal
                    v-if="audioRecorderOpen"
                    :chat-id="chatId"
                    @send="sendRecording"
                    @close="audioRecorderOpen = false"
                />

                <!-- Одна строка: файл · редактор · [аудио|эмодзи] · [видео|отправить].
                     Есть текст/файл → аудио→эмодзи, видео→кнопка отправки. -->
                <div class="chat-editor__row">
                    <AppButtonSmall
                        :size="34"
                        :disabled="disabled"
                        title="Файл"
                        @click="uploaderRef?.open()"
                    >
                        <IconFile />
                    </AppButtonSmall>

                    <AppEditor
                        ref="editorRef"
                        v-model="sendText"
                        :autofocus="true"
                        :disabled="disabled"
                        @input="emit('typing')"
                        @submit="send"
                    />

                    <!-- Эмодзи — всегда на месте; клик открывает пикер
                         (то же AppMenu, что у контекстного меню). -->
                    <AppMenu placement="top-end" :offset="8">
                        <template #trigger="{ isOpen }">
                            <AppButtonSmall
                                :size="34"
                                :disabled="disabled"
                                :active="isOpen"
                                title="Эмодзи"
                            >
                                <IconSmile />
                            </AppButtonSmall>
                        </template>
                        <template #default="{ close }">
                            <div class="chat-editor__emoji-grid">
                                <button
                                    v-for="emoji in EMOJIS"
                                    :key="emoji"
                                    class="chat-editor__emoji"
                                    @click="
                                        insertEmoji(emoji);
                                        close();
                                    "
                                >
                                    {{ emoji }}
                                </button>
                            </div>
                        </template>
                    </AppMenu>

                    <!-- Слот фиксированной ширины (34+4+34): без текста — аудио и
                         видео, с текстом — кнопка отправки на всю ширину слота.
                         Ширина не меняется → строка не дёргается. -->
                    <div class="chat-editor__slot">
                        <Transition name="mc-swap" mode="out-in">
                            <div
                                v-if="!canSend"
                                key="media"
                                class="chat-editor__media"
                            >
                                <AppButtonSmall
                                    :size="34"
                                    :disabled="disabled"
                                    title="Аудио"
                                    @click="audioRecorderOpen = true"
                                >
                                    <IconMic />
                                </AppButtonSmall>
                                <AppButtonSmall
                                    :size="34"
                                    :disabled="disabled"
                                    title="Видео"
                                    @click="videoRecorderOpen = true"
                                >
                                    <IconVideo />
                                </AppButtonSmall>
                            </div>
                            <button
                                v-else
                                key="send"
                                class="chat-editor__send"
                                :disabled="disabled"
                                aria-label="Отправить"
                                @click="send"
                            >
                                <span class="chat-editor__send-label"
                                    >Отправить</span
                                >
                            </button>
                        </Transition>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.chat-editor {
    position: relative;
    padding: 0 21px 12px;
    // Фон общий с лентой сообщений (задан на .chat-view__body) — здесь прозрачно.
    background: transparent;
    margin-top: 10px;

    // Оверлей при перетаскивании файлов на область ввода.
    &__drop {
        position: absolute;
        inset: 0 21px 12px;
        z-index: 5;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1.5px dashed var(--mc-acid);
        border-radius: 4px;
        background: color-mix(in srgb, var(--mc-acid) 8%, var(--mc-bg-input));
        color: var(--mc-acid);
        font-family: var(--mc-mono);
        font-size: 13px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        pointer-events: none;
    }

    // Рамка панели ввода: ряд ввода + панель инструментов.
    &__box {
        background: var(--mc-bg-input, var(--mc-bg-rail));
        border: 1px solid var(--mc-line);
        transition: border-color 0.15s;
        border-radius: 2px;

        &:focus-within {
            border-color: var(--mc-line-hard);
        }
    }

    // Одна строка: файл · редактор · эмодзи · аудио · видео · отправить.
    &__row {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px;
    }

    // Ряд режима правки (редактор + Сохранить/Отмена).
    &__input-row {
        display: flex;
        align-items: stretch;
        gap: 8px;
        padding: 4px 4px 4px 12px;
    }

    // Иконки внутри кнопок панели (AppButtonSmall / .mc-btn-sm).
    &__row :deep(.mc-btn-sm svg) {
        width: 18px;
        height: 18px;
    }

    // Слот справа: фикс. ширина = аудио + gap + видео (34+4+34), чтобы свап на
    // кнопку отправки не сдвигал редактор и эмодзи.
    &__slot {
        flex-shrink: 0;
        width: 72px;
    }

    &__media {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    // Пикер эмодзи (контент AppMenu): сетка как в контекстном меню, но шире.
    &__emoji-grid {
        display: grid;
        grid-template-columns: repeat(7, 38px);
        justify-content: center;
        gap: 2px;
        max-height: 300px;
        padding: 8px;
        overflow-y: auto;
    }

    &__emoji {
        width: 38px;
        height: 38px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 22px;
        line-height: 1;
        font-family:
            "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji",
            sans-serif;
        font-variant-emoji: emoji;
        border-radius: 2px;
        cursor: pointer;
        transition: background 0.1s;

        &:hover {
            background: var(--mc-bg-sel);
        }
    }

    // Кнопка отправки: залитая acid-пилюля на всю ширину слота. Стрелка
    // появляется сразу, подпись «Отправить» выезжает следом слева.
    &__send {
        width: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
        height: 30px;
        padding: 0 4px;
        color: #0a0c0a;
        background: var(--mc-acid);
        border: none;
        cursor: pointer;
        box-shadow: 0 0 20px var(--mc-acid-glow);
        transition: box-shadow 0.15s;

        &:hover {
            box-shadow: 0 0 32px var(--mc-acid-glow-strong);
        }

        &:disabled {
            opacity: 0.4;
            pointer-events: none;
        }
    }

    &__send-label {
        white-space: nowrap;
        font-family: var(--mc-mono);
        // Ужато под фикс. ширину слота (72px): подпись + стрелка впритык.
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        // Подпись вплывает следом за стрелкой (fade + слайд, без обрезки ширины).
        // animation: chat-editor-send-label 0.2s 0.06s both
        //     cubic-bezier(0.16, 1, 0.3, 1);
    }

    &__reply {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
        padding: 6px 8px;
        background: var(--mc-bg-rail);
        border: 1px solid var(--mc-line);
    }

    &__reply-bar {
        flex-shrink: 0;
        width: 2px;
        align-self: stretch;
        min-height: 26px;
        background: var(--mc-acid);
    }

    &__reply-body {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        line-height: 1.25;
    }

    &__reply-author {
        font-size: 11px;
        font-weight: 700;
        color: var(--mc-acid);
    }

    &__reply-text {
        overflow: hidden;
        font-size: 13px;
        color: var(--mc-fg-mute);
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    &__reply-cancel {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        color: var(--mc-fg-mute);
        background: none;
        border: none;
        cursor: pointer;

        &:hover {
            color: var(--mc-fg);
        }
    }

    &__btn {
        flex-shrink: 0;
        padding: 4px 12px;
        font-size: 13px;
        color: var(--mc-fg-mute);
        border: 1px solid var(--mc-line);
        transition: 0.1s;
        white-space: nowrap;

        &:hover {
            color: var(--mc-fg);
            background: var(--mc-bg-sel);
        }
    }
}

// Свап кнопок в слотах (аудио↔эмодзи, видео↔отправка): fade + scale, по очереди.
.mc-swap-enter-active,
.mc-swap-leave-active {
    transition:
        opacity 0.13s ease,
        transform 0.13s ease;
}
.mc-swap-enter-from,
.mc-swap-leave-to {
    opacity: 0;
    transform: scale(0.8);
}

// Подпись «Отправить»: fade + слайд справа (без морфинга ширины — не клипает текст).
@keyframes chat-editor-send-label {
    from {
        opacity: 1;
        transform: translateX(10px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}
</style>
